/**
 * PB-window → PH10-route lookup, injected rather than imported (ADR-0045).
 *
 * `src/shared/` must not depend on `src/features/` (dep-cruiser `shared-not-from-features`),
 * and there is not a single `shared → app` import in the tree today. The map the Quick Window
 * needs — `app/nav.ts WINDOW_TO_ROUTE` — lives on the far side of both edges, so `app/App.tsx`
 * (which already imports every layer) calls {@link registerQuickWindowRoutes} at module scope
 * and this module stays dependency-free.
 *
 * Unregistered is a legitimate state: a unit test that renders the dialog in isolation never
 * calls App, and the resolver then falls back to templates only (see
 * `resolveQuickWindowTarget`).
 */

/** The subset of `app/nav.ts RouteEntry` the Quick Window reads. Structurally compatible. */
export type QuickWindowRouteEntry = {
  to: string;
  /** Maps to the generic `/pb/:window` placeholder page — listed but not navigable. */
  placeholder?: boolean;
  /** PB creates the menu item `visible = false`: unreachable in the running PB app, so it must
   *  be unreachable here too (ADR-0028). Filtered OUT of the list entirely. */
  hiddenInPb?: boolean;
  /** Pulled from the running PH10 app while it finishes. Also filtered out. */
  devDisabled?: boolean;
};

let registry: Readonly<Record<string, QuickWindowRouteEntry>> | null = null;

/** Called once, from `app/App.tsx`, with `WINDOW_TO_ROUTE`. */
export function registerQuickWindowRoutes(
  map: Readonly<Record<string, QuickWindowRouteEntry>>,
): void {
  registry = map;
}

/** True once App has injected the map. False in isolated unit tests. */
export function hasQuickWindowRoutes(): boolean {
  return registry !== null;
}

/** Route for a PB window name (mode suffix already stripped). */
export function lookupRoute(window: string): QuickWindowRouteEntry | undefined {
  return registry?.[window];
}

/** Test hook. */
export function resetQuickWindowRoutes(): void {
  registry = null;
}
